import { COUNTER_LOCALE_ATTR, COUNTER_VALUE_ATTR } from '@/lib/counter-contract'
import { formatNumber } from '@/lib/utils'
import { useLayoutEffect, useRef, useState } from 'react'
import type { Locale } from 'shared'
import { useLingui } from '@lingui/react'

/**
 * The frozen `initialText` children reserve layout space on first paint / SSR and never change, so React's
 * reconciler never clobbers the ref-managed textContent.
 */

// Settles in ~0.6s.
const DECAY_PER_SECOND = 8

export function AnimateNumber(props: {
  locale?: Locale
  value: number
  suffix?: string
  testId?: string
  onAnimateComplete?: () => void
  decimalDigits?: number
  className?: string
}) {
  const { i18n } = useLingui()
  const locale = props.locale ?? (i18n.locale as Locale)

  const decimalDigits = props.decimalDigits ?? 0
  const ref = useRef<HTMLDivElement>(null)
  const rafRef = useRef(0)
  const currentRef = useRef(props.value ?? 0)
  const isFirstRef = useRef(true)
  const onCompleteRef = useRef(props.onAnimateComplete)
  const fmt = (n: number) => `${formatNumber(locale, n, decimalDigits)}${props.suffix ? ` ${props.suffix}` : ''}`
  const fmtRef = useRef(fmt)
  // Synced at commit, never during render: an in-render ref write bails the React Compiler out of memoizing this.
  // Declared before the animation effect so it runs first on a value-change commit.
  useLayoutEffect(() => {
    onCompleteRef.current = props.onAnimateComplete
    fmtRef.current = fmt
  })

  const [initialText] = useState(() => fmt(props.value ?? 0))

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return undefined

    // Record only — React already rendered {initialText}, and writing textContent here detaches its text node.
    if (isFirstRef.current) {
      isFirstRef.current = false
      currentRef.current = props.value ?? 0
      return undefined
    }

    const target = props.value ?? 0
    const targetText = fmtRef.current(target)

    // Show current value immediately (before first rAF) to prevent flash
    el.textContent = fmtRef.current(currentRef.current)

    let prev = 0

    const tick = (now: number) => {
      // rAF timestamps only: mixing in performance.now() can make the first dt negative.
      const dt = prev ? (now - prev) / 1000 : 0
      prev = now

      // Framerate-independent exponential decay toward the target.
      currentRef.current += (target - currentRef.current) * (1 - Math.exp(-DECAY_PER_SECOND * dt))

      const text = fmtRef.current(currentRef.current)

      // Stop once the formatted text matches — further ticks would only move the invisible float.
      if (text === targetText) {
        currentRef.current = target
        el.textContent = targetText
        onCompleteRef.current?.()
        return
      }

      if (el.textContent !== text) {
        el.textContent = text
      }

      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)

    return () => cancelAnimationFrame(rafRef.current)
  }, [props.value])

  return (
    <div
      ref={ref}
      className={`inline-block tracking-tight tabular-nums${props.className ? ` ${props.className}` : ''}`}
      data-testid={props.testId}
      // The hydration witness pair (see lib/counter-contract.ts). `initialText` below is the LIVE figure
      // on the very first paint, so every counter in this app renders a real value on both sides and any
      // disagreement between these attributes and the text beside them is a genuine divergence.
      {...{ [COUNTER_VALUE_ATTR]: props.value, [COUNTER_LOCALE_ATTR]: locale }}
    >
      {initialText}
    </div>
  )
}
